// components/PdfUploadContent.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  FaCloudUploadAlt,
  FaFilePdf,
  FaTrash,
  FaEye,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSearch,
  FaBuilding,
  FaUpload,
  FaChartBar,
  FaHistory,
  FaCog,
  FaDatabase,
  FaFolderOpen,
  FaFileContract
} from 'react-icons/fa';
import CONFIG from '../config';
import s3UploadAPI from '../api/s3Upload';
import companiesAPI from '../api/companies';

const PdfUploadContent = () => {
  // ========== STATE VARIABLES ==========
  const [documents, setDocuments] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // UI States
  const [showDocumentList, setShowDocumentList] = useState(false);
  const [showUploadResult, setShowUploadResult] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [apiError, setApiError] = useState(null);

  // File input reference
  const fileInputRef = useRef(null);
  const companyInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // ========== EFFECTS ==========
  
  // Load company suggestions when company name changes
  useEffect(() => {
    let isMounted = true;
    
    const loadCompanySuggestions = async () => {
      const trimmedCompanyName = companyName.trim();
      
      if (trimmedCompanyName.length < 2) {
        if (isMounted) {
          setCompanySuggestions([]);
          setShowSuggestions(false);
          setApiError(null);
        }
        return;
      }
      
      if (isMounted) {
        setLoadingSuggestions(true);
        setApiError(null);
      }
      
      try {
        const response = await companiesAPI.getSuggestions(trimmedCompanyName, 10);
        
        if (!isMounted) return;
        
        let suggestions = [];
        
        if (response && response.success !== false) {
          if (Array.isArray(response)) {
            suggestions = response;
          } else if (response.companies && Array.isArray(response.companies)) {
            suggestions = response.companies;
          } else if (response.data && Array.isArray(response.data)) {
            suggestions = response.data;
          } else if (response.suggestions && Array.isArray(response.suggestions)) {
            suggestions = response.suggestions;
          } else if (response.results && Array.isArray(response.results)) {
            suggestions = response.results;
          } else if (typeof response === 'object') {
            suggestions = Object.values(response).find(val => Array.isArray(val)) || [];
          }
        } else if (response && response.error) {
          setApiError(response.error);
          suggestions = [];
        }
        
        setCompanySuggestions(suggestions);
        
        if (suggestions.length > 0) {
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
          setApiError('No companies found');
        }
        
      } catch (error) {
        if (!isMounted) return;
        
        setApiError(error.message || 'Failed to load suggestions');
        setCompanySuggestions([]);
        setShowSuggestions(false);
        
        // Provide fallback suggestions for testing
        if (process.env.NODE_ENV === 'development') {
          const fallbackSuggestions = [
            { id: 1, name: 'Apple Inc.' },
            { id: 2, name: 'Microsoft Corporation' },
            { id: 3, name: 'Amazon.com Inc.' },
            { id: 4, name: 'Alphabet Inc.' },
            { id: 5, name: 'Tesla Inc.' }
          ].filter(company => 
            company.name.toLowerCase().includes(trimmedCompanyName.toLowerCase())
          );
          
          if (fallbackSuggestions.length > 0) {
            setCompanySuggestions(fallbackSuggestions);
            setShowSuggestions(true);
            setApiError(null);
          }
        }
      } finally {
        if (isMounted) {
          setLoadingSuggestions(false);
        }
      }
    };
    
    const timer = setTimeout(loadCompanySuggestions, 500);
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [companyName]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        companyInputRef.current && 
        !companyInputRef.current.contains(event.target) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ========== DOCUMENT HANDLING ==========

  const handleCompanySelect = (company) => {
    if (typeof company === 'object' && company.name) {
      setCompanyName(company.name);
    } else if (typeof company === 'string') {
      setCompanyName(company);
    } else {
      const companyNameStr = company?.name || company?.company_name || company?.title || String(company);
      setCompanyName(companyNameStr);
    }
    
    setShowSuggestions(false);
    setCompanySuggestions([]);
    setApiError(null);
  };

  const handleDocumentUpload = (files) => {
    const fileList = Array.from(files);
    
    // Filter only PDF files
    const pdfFiles = fileList.filter(
      (file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );

    // Check maximum files limit
    const maxFiles = 15;
    if (documents.length + pdfFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} documents allowed per upload`);
      return;
    }

    // Add new documents with metadata
    const newDocuments = pdfFiles.map((file) => {
      const isValid = file.type === 'application/pdf' && file.size <= 50 * 1024 * 1024;
      
      return {
        file,
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
        sizeBytes: file.size,
        uploadTime: new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        status: 'pending',
        message: '',
        isValid: isValid
      };
    });

    const validDocuments = newDocuments.filter(d => d.isValid);
    const invalidDocuments = newDocuments.filter(d => !d.isValid);
    
    if (invalidDocuments.length > 0) {
      alert(`${invalidDocuments.length} document(s) were invalid. Please upload PDF files under 50MB.`);
    }
    
    if (validDocuments.length > 0) {
      setDocuments((prev) => [...prev, ...validDocuments]);
      setUploadResult(null);
    }
  };

  const handleDocumentDrop = (e) => {
    e.preventDefault();
    handleDocumentUpload(e.dataTransfer.files);
  };

  const removeDocument = (id) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  const clearAllDocuments = () => {
    if (documents.length > 0 && window.confirm('Remove all documents?')) {
      setDocuments([]);
    }
  };

  // ========== UPLOAD PROCESSING ==========

  const processDocuments = async (e) => {
    e.preventDefault();

    // Validation
    if (!companyName.trim()) {
      alert('Please select a company');
      return;
    }

    if (documents.length === 0) {
      alert('Please add at least one document');
      return;
    }

    const invalidDocuments = documents.filter(d => !d.isValid);
    if (invalidDocuments.length > 0) {
      alert(`${invalidDocuments.length} document(s) are invalid. Please remove them before proceeding.`);
      return;
    }

    setProcessing(true);
    setUploadResult(null);

    try {
      // Update all documents to processing status
      setDocuments(prev => prev.map(doc => ({
        ...doc,
        status: 'processing',
        message: 'Processing...'
      })));

      // Extract file objects
      const files = documents.map(d => d.file);
      
      // Call the API
      const result = await s3UploadAPI.uploadMultiplePDFs(
        companyName,
        files,
        overwriteExisting
      );

      if (result.success) {
        setUploadResult({
          success: true,
          data: result,
          message: `Successfully processed ${result.summary?.successful_uploads || documents.length} documents`
        });

        // Update document statuses
        if (result.file_results) {
          const updatedDocuments = documents.map(doc => {
            const fileResult = result.file_results.find(
              fr => fr.filename === doc.name
            );
            
            if (fileResult) {
              return {
                ...doc,
                status: fileResult.status === 'uploaded' ? 'completed' : 
                       fileResult.status === 'skipped' ? 'skipped' : 'failed',
                message: fileResult.reason || fileResult.action || ''
              };
            }
            return doc;
          });
          setDocuments(updatedDocuments);
        } else {
          // Mark all as completed if no detailed results
          const updatedDocuments = documents.map(doc => ({
            ...doc,
            status: 'completed',
            message: 'Processed successfully'
          }));
          setDocuments(updatedDocuments);
        }

        setShowUploadResult(true);

      } else {
        throw new Error(result.message || 'Processing failed');
      }

    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Processing failed. Please try again.';
      
      setUploadResult({
        success: false,
        message: errorMessage,
        error: error.response?.data
      });

      // Mark all documents as failed
      const updatedDocuments = documents.map(doc => ({
        ...doc,
        status: 'failed',
        message: 'Processing failed'
      }));
      setDocuments(updatedDocuments);

      setShowUploadResult(true);
    } finally {
      setProcessing(false);
    }
  };

  // ========== UI COMPONENTS ==========

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FaCheckCircle className="text-emerald-500" />;
      case 'failed':
        return <FaExclamationTriangle className="text-rose-500" />;
      case 'processing':
        return <FaSpinner className="animate-spin text-blue-500" />;
      case 'skipped':
        return <FaExclamationTriangle className="text-amber-500" />;
      default:
        return <FaFilePdf className="text-slate-400" />;
    }
  };

  const StatCard = ({ title, value, description, icon: Icon, color }) => (
    <div className={`bg-white rounded-lg border ${color} p-5 hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-600">{title}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {description && (
            <p className="text-xs text-slate-500 mt-1">{description}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color.replace('border-', 'bg-').replace('-200', '-50')}`}>
          <Icon className={`text-xl ${color.includes('emerald') ? 'text-emerald-600' : 
                           color.includes('blue') ? 'text-blue-600' : 
                           color.includes('purple') ? 'text-purple-600' : 'text-slate-600'}`} />
        </div>
      </div>
    </div>
  );

  const DocumentCard = ({ document }) => (
    <div className={`border-l-4 ${document.status === 'completed' ? 'border-emerald-500 bg-emerald-50/30' : 
                    document.status === 'failed' ? 'border-rose-500 bg-rose-50/30' : 
                    document.status === 'processing' ? 'border-blue-500 bg-blue-50/30' : 'border-slate-300'} 
                    rounded-lg p-4 transition-all hover:shadow-sm`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1">
          {getStatusIcon(document.status)}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-slate-900 text-sm truncate">{document.name}</h4>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
              <span>{document.size} MB</span>
              <span>•</span>
              <span>{document.uploadTime}</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => removeDocument(document.id)}
          className="text-slate-400 hover:text-rose-500 transition-colors p-1"
          title="Remove document"
        >
          <FaTrash size={14} />
        </button>
      </div>
      {document.message && (
        <p className="text-xs text-slate-600 mt-2 pl-7">{document.message}</p>
      )}
    </div>
  );

  // ========== MAIN RENDER ==========
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-white rounded-xl shadow-sm border">
              <FaChartBar className="text-2xl text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Financial Document Portal</h1>
              <p className="text-slate-600 mt-1">Secure document submission and processing system</p>
            </div>
          </div>

          {/* Dashboard Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            <StatCard
              title="Documents Ready"
              value={documents.length}
              description="Awaiting processing"
              icon={FaFileContract}
              color="border-blue-200"
            />
            <StatCard
              title="Total Volume"
              value={`${(documents.reduce((sum, d) => sum + d.sizeBytes, 0) / (1024 * 1024)).toFixed(2)} MB`}
              description="Combined file size"
              icon={FaDatabase}
              color="border-emerald-200"
            />
            <StatCard
              title="Processing Mode"
              value={overwriteExisting ? 'Replace Mode' : 'Standard Mode'}
              description={overwriteExisting ? 'Existing files will be replaced' : 'Skip duplicate files'}
              icon={FaCog}
              color="border-purple-200"
            />
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Company & Documents */}
          <div className="lg:col-span-2 space-y-8">
            {/* Company Selection Card - Now takes half width */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <FaBuilding className="text-xl text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Company Information</h2>
                  <p className="text-slate-500 text-sm mt-1">Select the entity for document processing</p>
                </div>
              </div>

              <div className="space-y-6" ref={companyInputRef}>
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Company Entity *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => companyName.trim().length >= 2 && setShowSuggestions(true)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white placeholder-slate-400 transition-all"
                      placeholder="Type company name (min 2 characters)..."
                      required
                    />
                    <div className="absolute right-4 top-3.5">
                      {loadingSuggestions ? (
                        <FaSpinner className="animate-spin text-blue-500" />
                      ) : (
                        <FaSearch className="text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Suggestions Dropdown */}
                  {showSuggestions && (
                    <div 
                      ref={suggestionsRef}
                      className="mt-2 border border-slate-200 rounded-xl bg-white shadow-lg z-50 absolute w-full max-w-md"
                    >
                      {loadingSuggestions ? (
                        <div className="p-4 text-center">
                          <FaSpinner className="animate-spin text-blue-500 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">Loading suggestions...</p>
                        </div>
                      ) : apiError ? (
                        <div className="p-4 text-center">
                          <FaExclamationTriangle className="text-amber-500 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">{apiError}</p>
                          <p className="text-xs text-slate-500 mt-1">Try typing more characters</p>
                        </div>
                      ) : companySuggestions.length === 0 ? (
                        <div className="p-4 text-center">
                          <FaSearch className="text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">No companies found</p>
                          <p className="text-xs text-slate-500 mt-1">Try a different search term</p>
                        </div>
                      ) : (
                        <>
                          <div className="p-3 border-b border-slate-100">
                            <p className="text-xs font-medium text-slate-600">
                              {companySuggestions.length} companies found
                            </p>
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {companySuggestions.map((company, index) => (
                              <button
                                key={company.id || index}
                                onClick={() => handleCompanySelect(company)}
                                className="w-full px-4 py-3 text-left hover:bg-blue-50/50 border-b border-slate-100 last:border-b-0 transition-colors"
                              >
                                <span className="font-medium text-slate-800 block">
                                  {company.name || company.company_name || company.title || String(company)}
                                </span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-slate-700">Processing Options</h3>
                      <p className="text-xs text-slate-500 mt-1">Configure document handling behavior</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overwriteExisting}
                        onChange={(e) => setOverwriteExisting(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {overwriteExisting ? 
                      'Existing documents will be replaced with new versions' : 
                      'Duplicate documents will be skipped automatically'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Document Upload Card - Simplified Design */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <FaCloudUploadAlt className="text-xl text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Document Submission</h2>
                  <p className="text-slate-500 text-sm mt-1">Upload financial statements and reports</p>
                </div>
              </div>

              {/* Upload Zone - Simplified */}
              <div
                onDrop={handleDocumentDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current.click()}
                className="group"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf"
                  multiple
                  onChange={(e) => handleDocumentUpload(e.target.files)}
                />

                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/20 transition-all">
                  <div className="flex flex-col items-center">
                    <FaUpload className="text-2xl text-blue-600 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      Upload PDF Documents
                    </h3>
                    <p className="text-slate-600 mb-4 max-w-sm text-sm">
                      Click to browse or drag and drop PDF files here
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium">
                      <FaFolderOpen />
                      Select Files
                    </div>
                    <div className="flex items-center justify-center gap-3 text-xs text-slate-500 mt-4">
                      <span className="flex items-center gap-1">
                        <FaFilePdf className="text-rose-500" />
                        PDF Only
                      </span>
                      <span className="text-slate-300">•</span>
                      <span>Max 50MB per file</span>
                      <span className="text-slate-300">•</span>
                      <span>Max 15 files</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Actions */}
              {documents.length > 0 && (
                <div className="mt-6 pt-5 border-t border-slate-200 flex justify-between items-center">
                  <button
                    onClick={() => setShowDocumentList(true)}
                    className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <FaEye />
                    View All ({documents.length})
                  </button>
                  <button
                    onClick={clearAllDocuments}
                    className="flex items-center gap-2 px-4 py-2 text-rose-600 hover:text-rose-800 text-sm font-medium hover:bg-rose-50 rounded-lg transition-colors"
                  >
                    <FaTrash />
                    Clear All
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Processing Panel */}
          <div className="space-y-8">
            {/* Processing Summary Card - Removed ticker symbol */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <FaChartBar className="text-xl text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Processing Summary</h2>
                  <p className="text-slate-500 text-sm mt-1">Review and initiate processing</p>
                </div>
              </div>

              <div className="space-y-5 mb-8">
                <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                  <div>
                    <p className="text-sm text-slate-600">Company</p>
                    <p className="font-medium text-slate-900 mt-0.5">{companyName || '—'}</p>
                  </div>
                  <FaBuilding className="text-slate-400" />
                </div>
                
                <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                  <div>
                    <p className="text-sm text-slate-600">Documents Ready</p>
                    <p className="text-2xl font-bold text-slate-900 mt-0.5">{documents.length}</p>
                  </div>
                  <FaFileContract className="text-slate-400" />
                </div>
                
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-600">Processing Mode</p>
                    <p className={`font-medium ${overwriteExisting ? 'text-amber-600' : 'text-emerald-600'} mt-0.5`}>
                      {overwriteExisting ? 'Replace Existing' : 'Standard'}
                    </p>
                  </div>
                  <FaCog className="text-slate-400" />
                </div>
              </div>

              <button
                onClick={processDocuments}
                disabled={processing || !companyName.trim() || documents.length === 0}
                className="w-full py-3 px-4 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl font-semibold hover:from-slate-800 hover:to-slate-700 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
              >
                {processing ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCloudUploadAlt />
                    Begin Processing
                  </>
                )}
              </button>

              {processing && (
                <div className="mt-5">
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 animate-pulse w-2/3"></div>
                  </div>
                  <p className="text-xs text-center text-slate-500 mt-2.5">
                    Processing documents securely...
                  </p>
                </div>
              )}
            </div>

            {/* Recent Documents Preview */}
            {documents.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-slate-900">Recent Documents</h3>
                  <span className="text-sm text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                    {documents.length} total
                  </span>
                </div>
                <div className="space-y-3">
                  {documents.slice(0, 3).map((doc) => (
                    <DocumentCard key={doc.id} document={doc} />
                  ))}
                  {documents.length > 3 && (
                    <button
                      onClick={() => setShowDocumentList(true)}
                      className="w-full py-2.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg border border-dashed border-slate-300 transition-colors"
                    >
                      View {documents.length - 3} more documents
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* System Status */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white rounded-lg border border-slate-200">
                  <FaDatabase className="text-slate-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-800">System Status</h4>
                  <p className="text-xs text-slate-500">Secure document processing</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Processing Engine</span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-emerald-600 font-medium text-sm">Online</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Storage Platform</span>
                  <span className="text-sm font-medium text-slate-900">Secure Document Vault</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Encryption</span>
                  <span className="text-sm font-medium text-slate-900">256-bit AES</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document List Modal */}
      {showDocumentList && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Document Queue</h3>
                <p className="text-slate-600 text-sm mt-1">
                  {documents.length} documents • {(documents.reduce((sum, d) => sum + d.sizeBytes, 0) / (1024 * 1024)).toFixed(2)} MB total
                </p>
              </div>
              <button
                onClick={() => setShowDocumentList(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <span className="sr-only">Close</span>
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {documents.length === 0 ? (
                <div className="text-center py-12">
                  <FaFileContract className="text-4xl text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No documents in queue</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <DocumentCard key={doc.id} document={doc} />
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-slate-200">
              <div className="flex justify-between items-center">
                <button
                  onClick={clearAllDocuments}
                  className="px-4 py-2.5 text-rose-600 hover:bg-rose-50 rounded-lg font-medium transition-colors"
                >
                  Clear All Documents
                </button>
                <button
                  onClick={() => setShowDocumentList(false)}
                  className="px-4 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Processing Result Modal */}
      {showUploadResult && uploadResult && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-4">
                {uploadResult.success ? (
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <FaCheckCircle className="text-3xl text-emerald-600" />
                  </div>
                ) : (
                  <div className="p-3 bg-rose-100 rounded-xl">
                    <FaExclamationTriangle className="text-3xl text-rose-600" />
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">
                    {uploadResult.success ? 'Processing Complete' : 'Processing Failed'}
                  </h3>
                  <p className="text-slate-600 text-sm mt-1">
                    {uploadResult.success ? 'Documents processed successfully' : 'Unable to complete processing'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <p className="mb-6">{uploadResult.message}</p>
              
              {uploadResult.data?.summary && (
                <div className="bg-slate-50 p-5 rounded-xl mb-6">
                  <h4 className="font-medium text-slate-800 mb-4">Processing Summary</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-sm text-slate-600">Total Documents:</div>
                    <div className="font-medium text-right">{uploadResult.data.summary.total_files}</div>
                    
                    <div className="text-sm text-emerald-600">Processed:</div>
                    <div className="font-medium text-emerald-600 text-right">
                      {uploadResult.data.summary.successful_uploads}
                    </div>
                    
                    {uploadResult.data.summary.failed_uploads > 0 && (
                      <>
                        <div className="text-sm text-rose-600">Failed:</div>
                        <div className="font-medium text-rose-600 text-right">
                          {uploadResult.data.summary.failed_uploads}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-slate-200">
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowUploadResult(false);
                    if (uploadResult.success) {
                      setDocuments([]);
                    }
                  }}
                  className="px-5 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
                >
                  {uploadResult.success ? 'Upload More Documents' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfUploadContent;